/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { ButtonIcon } from "../../../components/Button/Button"
import { faClockRotateLeft, faClose, faFilePdf } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useChecklist } from "../../../hooks/Checklist/useChecklist"
import type { ChecklistTemplate } from "../../../Objects/Checklist/ChecklistTemplate"
import type { ChecklistSector } from "../../../Objects/Checklist/ChecklistSector"
import type { ChecklistExecutionSummary } from "../../../Objects/Checklist/ChecklistExecutionSummary"
import { ChecklistExecutionDetailsPopup } from "./ChecklistExecutionDetailsPopup"

interface Props {
  templates: ChecklistTemplate[]
  sectors: ChecklistSector[]
  onClose: () => void
}

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10)

export const ChecklistHistoryPopup: React.FC<Props> = ({ templates, sectors, onClose }) => {
  const { t } = useTranslation(["pages", "general", "helpPopup"])

  const { getHistory, downloadHistoryPdf, isLoading } = useChecklist()

  const [startDate, setStartDate] = useState(() => toIsoDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)))

  const [endDate, setEndDate] = useState(() => toIsoDate(new Date()))

  const [templateId, setTemplateId] = useState("")

  const [sectorId, setSectorId] = useState("")

  const [executions, setExecutions] = useState<ChecklistExecutionSummary[]>([])

  const [detailsId, setDetailsId] = useState<number | null>(null)

  useEffect(() => {
    void (async () => {
      const result = await getHistory(
        startDate,
        endDate,
        templateId ? Number(templateId) : undefined,
        sectorId ? Number(sectorId) : undefined,
      )

      if (result) setExecutions(result)
    })()
  }, [startDate, endDate, templateId, sectorId])

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("checklists.history.title")}
              icon={faClockRotateLeft}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    icon={faFilePdf}
                    disabled={isLoading || executions.length === 0}
                    onClick={() =>
                      downloadHistoryPdf(
                        startDate,
                        endDate,
                        templateId ? Number(templateId) : undefined,
                        sectorId ? Number(sectorId) : undefined,
                      )
                    }
                    size="small"
                    descriptionKey="interactionDescriptions:pages.checklist.checklistHistoryPopup.button.downloadPdf"
                  />

                  <GetSpace right={5} />

                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.checklist-history"
                    title={t("helpPopup:global.checklistHistory.title")}
                    description={t("helpPopup:global.checklistHistory.description")}
                    bullets={t("helpPopup:global.checklistHistory.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.checklist.checklistHistoryPopup.button.onClose"
                  />
                </div>
              }
            />

            <div className={defaultStyle.divisionBar}></div>

            <div className="p-2">
              <div className="grid md:grid-cols-2 gap-2">
                <InputField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder={t("checklists.history.startDate")}
                  descriptionKey="interactionDescriptions:pages.checklist.checklistHistoryPopup.input.startDate"
                />
                <InputField
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder={t("checklists.history.endDate")}
                  descriptionKey="interactionDescriptions:pages.checklist.checklistHistoryPopup.input.endDate"
                />

                <GenericDropDown
                  data={[
                    { label: t("checklists.history.allTemplates"), value: "" },
                    ...templates.map((e) => ({ label: e.name, value: String(e.id) })),
                  ]}
                  initialState={templateId}
                  handlerFuncion={(e) => setTemplateId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.checklist.checklistHistoryPopup.select.template"
                />

                <GenericDropDown
                  data={[
                    { label: t("checklists.history.allSectors"), value: "" },
                    ...sectors.map((e) => ({ label: e.name, value: String(e.id) })),
                  ]}
                  initialState={sectorId}
                  handlerFuncion={(e) => setSectorId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.checklist.checklistHistoryPopup.select.sector"
                />
              </div>
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className="flex flex-col gap-2 p-2 max-h-[50vh] overflow-y-auto">
              {!isLoading && executions.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">{t("checklists.history.empty")}</p>
              )}

              {executions.map((execution) => (
                <Animate key={execution.id} type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <button
                    type="button"
                    onClick={() => setDetailsId(execution.id)}
                    data-interaction-description-key="interactionDescriptions:pages.checklist.checklistHistoryPopup.button.openDetails"
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border-2 border-slate-400 text-left"
                  >
                    <div className="min-w-0">
                      <span className="font-semibold text-slate-800 truncate block">{execution.templateName}</span>

                      <span className="text-xs text-slate-500">
                        {execution.operationalDate} · {execution.startedByEmployeeName}
                      </span>
                    </div>

                    <span
                      className={`px-2 py-1 text-xs rounded-full font-semibold shrink-0 ${
                        execution.status === "Completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {execution.completionPercentage}%
                    </span>
                  </button>
                </Animate>
              ))}
            </div>
          </section>
        </Animate>
      </div>

      {detailsId !== null && <ChecklistExecutionDetailsPopup id={detailsId} onClose={() => setDetailsId(null)} />}
    </ModalPortal>
  )
}
